import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';

const plans = {
  basic: {
    name: 'Basic',
    monthlyPrice: 29,
    yearlyPrice: 290,
    features: [
      'Up to 5 users',
      'Basic reporting',
      'Email support',
      '5GB storage',
      'Basic integrations'
    ],
    color: '#5c2d91'
  },
  professional: {
    name: 'Professional',
    monthlyPrice: 49,
    yearlyPrice: 490,
    features: [
      'Up to 20 users',
      'Advanced reporting',
      'Priority email support',
      '20GB storage',
      'Advanced integrations',
      'Custom workflows'
    ],
    color: '#19ba8b',
    recommended: true
  },
  enterprise: {
    name: 'Enterprise',
    monthlyPrice: 99,
    yearlyPrice: 990,
    features: [
      'Unlimited users',
      'Custom reporting',
      '24/7 phone support',
      'Unlimited storage',
      'Premium integrations',
      'Custom workflows',
      'Dedicated account manager'
    ],
    color: '#1976d2'
  }
};

const StyledCard = styled(Card)(({ theme, selected, plancolor }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  border: selected ? `2px solid ${plancolor}` : '1px solid rgba(0, 0, 0, 0.12)',
  boxShadow: selected ? theme.shadows[4] : theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

function SubscriptionPlan({ data, onNext, onBack }) {
  const [formData, setFormData] = useState(data);
  const [interval, setInterval] = useState(formData.interval || 'month');

  const handleIntervalChange = (event, newInterval) => {
    if (newInterval !== null) {
      setInterval(newInterval);
      setFormData(prev => ({ ...prev, interval: newInterval }));
    }
  };

  const handlePlanSelect = (planId) => {
    setFormData(prev => ({ ...prev, plan: planId }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.plan) {
      onNext({ subscription: formData });
    }
  };

  const getPrice = (plan) => {
    return interval === 'month' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Choose Your Plan
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={interval}
          exclusive
          onChange={handleIntervalChange}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="month">Monthly</ToggleButton>
          <ToggleButton value="year">
            Yearly
            <Chip
              label="Save 20%"
              size="small"
              color="secondary"
              sx={{ ml: 1 }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {Object.entries(plans).map(([planId, plan]) => (
          <Grid item xs={12} md={4} key={planId}>
            <StyledCard
              selected={formData.plan === planId}
              plancolor={plan.color}
              onClick={() => handlePlanSelect(planId)}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                {plan.recommended && (
                  <Chip
                    label="Recommended"
                    color="secondary"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}
                <Typography variant="h5" component="div" gutterBottom>
                  {plan.name}
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  ${getPrice(plan)}
                  <Typography variant="subtitle1" component="span" color="text.secondary">
                    /{interval}
                  </Typography>
                </Typography>
                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant={formData.plan === planId ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => handlePlanSelect(planId)}
                >
                  {formData.plan === planId ? "Selected" : "Select Plan"}
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!formData.plan}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default SubscriptionPlan; 